import { Fragment } from 'react'
import { Image } from '@educator/components/Image'
import type { SubjectDetailItem } from '@educator/types/subjects'

interface HeroDetailBarProps {
  details: SubjectDetailItem[]
  /** Mobile-only partner column: column label plus the logo's alt text. */
  partner: {
    label: string
    logoAlt: string
  }
}

/**
 * The two enrollment-driven columns (deadline + start date) lead the bar; the
 * static columns that follow hold longer copy and wrap inside a 168px measure.
 */
const ENROLLMENT_COLUMN_COUNT = 2

/** Mobile stacks the columns two-up; desktop lays all five in a single row. */
const MOBILE_COLUMN_COUNT = 2

const PARTNER_LOGO_URL =
  'https://static.masterclass.com/executive-program-details-chicago-booth.svg'

/** Intrinsic size of `PARTNER_LOGO_URL`. */
const PARTNER_LOGO_WIDTH = 122
const PARTNER_LOGO_HEIGHT = 28

export const HeroDetailBar = ({ details, partner }: HeroDetailBarProps) => (
  <div className='grid w-full grid-cols-2 gap-x-4 py-2 text-edu-fg-primary md:grid-cols-5 md:border-t md:border-edu-stroke-default md:pt-8 md:pb-10'>
    {details.map((item, index) => {
      const endsMobileRow =
        index % MOBILE_COLUMN_COUNT === MOBILE_COLUMN_COUNT - 1

      return (
        <Fragment key={item.label}>
          <div className='flex flex-col gap-2 pt-3 pb-4 md:py-0'>
            {/* label-1 stays the caps treatment (caps + tracking are baked in),
                but the Boogie caps scale tops out at 10px, so the size is
                bumped here. */}
            <span className='text-label-1 font-medium text-[12px]'>
              {item.label}
            </span>
            <span
              className={`text-body-3 leading-body-3 md:text-body-1 md:leading-body-1 ${index < ENROLLMENT_COLUMN_COUNT ? '' : 'md:max-w-[168px]'}`}
            >
              {item.value}
            </span>
          </div>
          {endsMobileRow && index < details.length - 1 && (
            // `p-0` is load-bearing: `col-span-2` matches the bootstrap-grid
            // override `[class^='col-']`, which sets a four-sided gutter padding.
            <div className='col-span-2 border-t border-edu-stroke-default p-0 md:hidden' />
          )}
        </Fragment>
      )
    })}
    {/* Mobile only: fills the odd column the five detail entries leave empty.
        Desktop lays those five out in a single row with no partner column. */}
    <div className='flex flex-col gap-2 pt-3 pb-4 md:hidden'>
      <span className='text-label-1 font-medium text-[12px]'>
        {partner.label}
      </span>
      <Image
        src={PARTNER_LOGO_URL}
        alt={partner.logoAlt}
        width={PARTNER_LOGO_WIDTH}
        height={PARTNER_LOGO_HEIGHT}
        className='h-auto max-w-full'
      />
    </div>
  </div>
)
